import { Box, Button, Typography } from '@mui/material';
import { Edit, History } from '@mui/icons-material';
import Moment from 'moment';
import { useQuery } from '@apollo/client/react';
import { gql } from '../../__generated__';
import { useConfig } from '../../components/Configuration';
import { formatDate } from '../../Formatters';
import { tokens } from './tokens';
import LoadingSpinner from '../../components/LoadingSpinner';

interface Change {
  id: string;
  isExternal: boolean;
  description: string;
  createdAt: string;
  createdByName: string;
}

const GET_CHANGES = gql(`
  query GetUserChangeLog($id: Int!, $offset: Int, $limit: Int) {
    changes: getUserChangeLog(id: $id, offset: $offset, limit: $limit) {
      id
      isExternal
      description
      createdAt
      createdByName
    }
  }
`);

interface Props {
  userId: number;
}

export function ChangesPanel({ userId }: Props) {
  const config = useConfig();
  const pollInterval = config?.defaultPollingInterval ?? 0;
  const { loading, error, data, fetchMore } = useQuery<{ changes: Change[] }>(GET_CHANGES, {
    variables: { id: userId, offset: 0, limit: 20 },
    pollInterval,
  });

  if (error) return <Box sx={{ p: { xs: 2, sm: 3 } }}><span>Feil! {error.message}</span></Box>;
  if (loading && !data) return <Box sx={{ p: { xs: 2, sm: 3 } }}><LoadingSpinner /></Box>;

  const changes = data?.changes ?? [];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ position: 'relative', pl: 1 }}>
        <Box
          sx={{
            position: 'absolute',
            left: 14,
            top: 8,
            bottom: 8,
            width: 2,
            background: tokens.border,
          }}
        />
        {changes.map((c, i) => (
          <Box
            key={c.id}
            sx={{
              display: 'flex',
              gap: 2,
              pb: i === changes.length - 1 ? 0 : '20px',
              position: 'relative',
            }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: c.isExternal ? '#EEF1F3' : tokens.primaryTint,
                color: c.isExternal ? tokens.textSecondary : tokens.primaryDark,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                position: 'relative',
                zIndex: 1,
                border: `2px solid ${tokens.surface}`,
              }}>
              {c.isExternal ? <History sx={{ fontSize: 14 }} /> : <Edit sx={{ fontSize: 14 }} />}
            </Box>
            <Box sx={{ flex: 1, pt: 0.25 }}>
              <Typography sx={{ fontSize: 14, color: tokens.text, lineHeight: 1.4 }}>{c.description}</Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  fontSize: 12,
                  color: tokens.textSecondary,
                  mt: 0.5,
                }}>
                <span>{c.createdByName}</span>
                <span style={{ color: tokens.textDisabled }}>·</span>
                <span title={formatDate(c.createdAt)}>{Moment(c.createdAt).fromNow()}</span>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Button
          size="small"
          sx={{ textTransform: 'none' }}
          onClick={() =>
            fetchMore({
              variables: { offset: changes.length },
              updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult?.changes?.length) return prev;
                return { ...prev, changes: [...prev.changes, ...fetchMoreResult.changes] };
              },
            })
          }>
          Last flere
        </Button>
      </Box>
    </Box>
  );
}
